if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.prefetchUrl = null;
        this.prefetchedText = null;
        this.prefetchPromise = null;
        this.drawerFormResizeObserver = null;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        this.bindPrefetchListeners();
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        this.resetDrawerActionsOffset();

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      disconnectedCallback() {
        this.resetDrawerActionsOffset();
      }

      show(opener) {
        const productUrl = this.getContextualProductUrl(opener.getAttribute('data-product-url'));
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        this.fetchProductMarkup(productUrl)
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            if (!productElement) {
              window.location = productUrl;
              return;
            }

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
            this.notifyQuickAddContentLoaded();
            requestAnimationFrame(() => this.notifyQuickAddContentLoaded());
            setTimeout(() => this.notifyQuickAddContentLoaded(), 250);
            this.injectBackInStockFallback(opener);
            requestAnimationFrame(() => this.injectBackInStockFallback(opener));
            setTimeout(() => this.injectBackInStockFallback(opener), 250);
            this.syncDrawerActionsOffset();
            requestAnimationFrame(() => this.syncDrawerActionsOffset());
            setTimeout(() => this.syncDrawerActionsOffset(), 250);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      bindPrefetchListeners() {
        const modalId = this.id;
        if (!modalId) return;

        document.querySelectorAll(`[data-modal="#${modalId}"] [data-product-url]`).forEach((button) => {
          if (button.dataset.quickAddPrefetchBound === 'true') return;
          button.dataset.quickAddPrefetchBound = 'true';

          const prefetchHandler = () => {
            const productUrl = this.getContextualProductUrl(button.getAttribute('data-product-url'));
            if (productUrl) this.prefetchProductMarkup(productUrl);
          };

          button.addEventListener('mouseenter', prefetchHandler, { once: true });
          button.addEventListener('focus', prefetchHandler, { once: true });
          button.addEventListener('touchstart', prefetchHandler, { once: true, passive: true });
        });
      }

      getContextualProductUrl(productUrl) {
        if (!productUrl) return productUrl;

        const contextualUrl = new URL(productUrl, window.location.origin);
        const currentParams = new URLSearchParams(window.location.search);

        currentParams.forEach((value, key) => {
          if (contextualUrl.searchParams.has(key)) return;
          contextualUrl.searchParams.append(key, value);
        });

        return `${contextualUrl.pathname}${contextualUrl.search}${contextualUrl.hash}`;
      }

      notifyQuickAddContentLoaded() {
        const productInfo = this.modalContent.querySelector('product-info');
        const sectionId = productInfo?.dataset?.originalSection || productInfo?.dataset?.section || null;
        const detail = {
          modalId: this.id,
          modal: this,
          productInfo,
          container: this.modalContent,
          sectionId,
        };

        document.dispatchEvent(new CustomEvent('quickadd:content:loaded', { detail }));
        window.dispatchEvent(new CustomEvent('quickadd:content:loaded', { detail }));

        if (productInfo && sectionId) {
          // Mirror the section lifecycle event shape used by Shopify editor-driven refreshes.
          productInfo.dispatchEvent(
            new CustomEvent('shopify:section:load', {
              bubbles: true,
              detail: { sectionId },
            })
          );
          document.dispatchEvent(
            new CustomEvent('shopify:section:load', {
              detail: { sectionId },
            })
          );
        }
      }

      injectBackInStockFallback(opener) {
        if (!this.classList.contains('quick-add-modal--drawer')) return;

        const productInfo = this.modalContent.querySelector('product-info');
        if (!productInfo) return;

        const productForm = productInfo.querySelector('product-form .form, form[action*="/cart/add"]');
        if (!productForm) return;

        const addButton = productForm.querySelector('.product-form__submit, button[name="add"]');
        if (!addButton || !addButton.disabled) return;

        const existingVisibleBisTrigger = productInfo.querySelector('.BIS_trigger, #BIS_trigger');
        if (existingVisibleBisTrigger) {
          const computed = window.getComputedStyle(existingVisibleBisTrigger);
          const isVisible =
            computed.display !== 'none' &&
            computed.visibility !== 'hidden' &&
            existingVisibleBisTrigger.getAttribute('aria-hidden') !== 'true';
          if (isVisible) return;
        }

        const productDataRaw = opener?.dataset?.bisProduct;
        if (!productDataRaw) return;

        let productData;
        try {
          productData = JSON.parse(productDataRaw);
        } catch (error) {
          return;
        }

        const variantId = Number(productForm.querySelector('input[name="id"]')?.value || 0);
        if (variantId) {
          productData.selected_variant_id = variantId;
        }

        if (productInfo.querySelector('.quick-add-bis-trigger')) return;

        const buttonContainer = productForm.querySelector('.product-form__buttons') || productForm;
        if (!buttonContainer) return;

        const bisTrigger = document.createElement('a');
        bisTrigger.href = '#';
        bisTrigger.className = 'quick-add-bis-trigger quick-add-bis-trigger--sticky BIS_trigger button button--full-width';
        bisTrigger.textContent = 'Benachrichtige mich, wenn verfügbar';
        bisTrigger.setAttribute('data-product-data', JSON.stringify(productData));
        bisTrigger.dataset.productData = JSON.stringify(productData);

        bisTrigger.addEventListener('click', (event) => {
          const popupShow = window.BIS?.popup?.show;
          if (!popupShow) return;
          event.preventDefault();
          try {
            if (variantId) popupShow({ variantId });
            else popupShow();
          } catch (error) {
            // Keep default BIS trigger behavior when custom invocation fails.
          }
        });

        if (buttonContainer.firstElementChild) {
          buttonContainer.insertBefore(bisTrigger, buttonContainer.firstElementChild);
        } else {
          buttonContainer.appendChild(bisTrigger);
        }
      }

      prefetchProductMarkup(productUrl) {
        if (!productUrl || this.prefetchUrl === productUrl) return;

        this.prefetchUrl = productUrl;
        this.prefetchedText = null;
        this.prefetchPromise = fetch(productUrl)
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          })
          .then((responseText) => {
            this.prefetchedText = responseText;
            return responseText;
          })
          .catch(() => {
            this.prefetchUrl = null;
            this.prefetchedText = null;
            return null;
          })
          .finally(() => {
            this.prefetchPromise = null;
          });
      }

      fetchProductMarkup(productUrl) {
        if (this.prefetchUrl === productUrl && this.prefetchedText) return Promise.resolve(this.prefetchedText);
        if (this.prefetchUrl === productUrl && this.prefetchPromise) {
          return this.prefetchPromise.then((responseText) => {
            if (responseText) return responseText;
            return fetch(productUrl).then((response) => response.text());
          });
        }

        return fetch(productUrl).then((response) => response.text());
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.preprocessDrawerContent(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preprocessDrawerContent(productElement) {
        if (!this.classList.contains('quick-add-modal--drawer')) return;

        productElement
          .querySelectorAll('.product__accordion, .product__description, .collapsible-description, .collapsible-row')
          .forEach((element) => element.remove());

        productElement.querySelectorAll('.product__tax').forEach((taxElement) => {
          const nextElement = taxElement.nextElementSibling;
          if (!nextElement) return;
          if (!nextElement.querySelector('form.installment')) return;

          const contentProbe = nextElement.cloneNode(true);
          contentProbe
            .querySelectorAll('input[type="hidden"], script, style, link, template')
            .forEach((node) => node.remove());

          const hasText = contentProbe.textContent.replace(/\s+/g, '').length > 0;
          const hasRenderableNodes = Boolean(
            contentProbe.querySelector('img, svg, video, iframe, model-viewer, button, select, textarea')
          );

          if (!hasText && !hasRenderableNodes) nextElement.remove();
        });

        const productRoot = productElement.querySelector('.product');
        if (productRoot) {
          productRoot.classList.remove(
            'product--small',
            'product--medium',
            'product--large',
            'product--left',
            'product--right',
            'grid--2-col',
            'grid--2-col-tablet',
            'grid--2-col-desktop'
          );
          productRoot.classList.add('product--quick-add-drawer-layout');

          productRoot.querySelectorAll('.product__media-wrapper, .product__info-wrapper').forEach((wrapper) => {
            wrapper.style.width = '100%';
            wrapper.style.maxWidth = '100%';
            wrapper.style.flexBasis = '100%';
          });
        }

        const infoContainer = productElement.querySelector('.product__info-container');
        if (!infoContainer) return;

        const productForm = infoContainer.querySelector('product-form');
        if (!productForm) return;

        const formWrapper = productForm.parentElement;
        if (formWrapper && formWrapper.parentElement === infoContainer) {
          infoContainer.appendChild(formWrapper);
          return;
        }

        infoContainer.appendChild(productForm);
      }

      syncDrawerActionsOffset() {
        if (!this.classList.contains('quick-add-modal--drawer')) return;

        const infoContainer = this.querySelector('.quick-add-modal__content-info');
        const actions = infoContainer?.querySelector('.product-form');
        if (!infoContainer || !actions) return;

        const setOffset = () => {
          infoContainer.style.setProperty('--quick-add-actions-height', `${Math.ceil(actions.getBoundingClientRect().height)}px`);
        };

        setOffset();
        if (this.drawerFormResizeObserver) this.drawerFormResizeObserver.disconnect();
        this.drawerFormResizeObserver = new ResizeObserver(() => setOffset());
        this.drawerFormResizeObserver.observe(actions);
      }

      resetDrawerActionsOffset() {
        if (this.drawerFormResizeObserver) {
          this.drawerFormResizeObserver.disconnect();
          this.drawerFormResizeObserver = null;
        }
        const infoContainer = this.querySelector('.quick-add-modal__content-info');
        if (infoContainer) infoContainer.style.removeProperty('--quick-add-actions-height');
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        productElement
          .querySelectorAll('.icon-with-text, product-recommendations, .product__view-details')
          .forEach((el) => el.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}

if (!window.__quickAddOverlayClickGuard) {
  window.__quickAddOverlayClickGuard = true;

  const stopOverlayPropagation = (event) => {
    const overlay = event.target.closest('.quick-add--overlay');
    if (!overlay) return;
    if (event.target.closest('button[disabled], .button[disabled], [aria-disabled="true"]')) {
      event.preventDefault();
    }
    event.stopPropagation();
  };

  const forwardCoveredQuickAddClicks = (event) => {
    if (typeof event.clientX !== 'number' || typeof event.clientY !== 'number') return;
    if (event.target.closest('.quick-add--overlay')) return;

    const overlays = document.querySelectorAll('.card.card--quick-add-overlay .quick-add--overlay');
    for (const overlay of overlays) {
      const button = overlay.querySelector('button');
      if (!button) continue;

      const rect = button.getBoundingClientRect();
      const withinX = event.clientX >= rect.left && event.clientX <= rect.right;
      const withinY = event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!withinX || !withinY) continue;

      event.preventDefault();
      event.stopPropagation();
      button.click();
      return;
    }
  };

  document.addEventListener('pointerdown', stopOverlayPropagation);
  document.addEventListener('click', stopOverlayPropagation);
  document.addEventListener('click', forwardCoveredQuickAddClicks, true);
}
